import { useState } from 'react';
import { Download, HatGlasses, Loader2 } from 'lucide-react';

import { resolveRedactionDownloadUrl } from '../../lib/redactionApi';
import type { RedactionManifest } from '../../types/redaction';

interface RedactionsListProps {
  redactions: RedactionManifest[];
  isLoading: boolean;
}

/**
 * Redacted copies already released from this record.
 *
 * Visible to anyone who can read the record, because a copy is only ever
 * accessible to those who could read its source. The list shows the grounds
 * the content was withheld under, which is what a later query about a release
 * asks about.
 */
export default function RedactionsList({
  redactions,
  isLoading,
}: RedactionsListProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (manifest: RedactionManifest, href: string) => {
    setDownloadingId(manifest.id);
    try {
      const url = await resolveRedactionDownloadUrl(href);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // The record page surfaces failures through its own toast; a failed
      // download here just leaves the row as it was.
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading redacted copies...
      </div>
    );
  }

  if (redactions.length === 0) {
    return (
      <p className="text-sm text-gray-400">
        No redacted copies have been created from this record.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
      {redactions.map((manifest) => {
        // The API says whether a copy can be downloaded; the interface
        // follows the link and never builds one itself.
        const downloadHref = manifest._links?.download?.href;
        return (
          <li
            key={manifest.id}
            className="flex items-start justify-between gap-4 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <HatGlasses className="h-4 w-4 shrink-0 text-gray-400" />
                <p className="text-sm font-semibold text-gray-900">
                  {manifest.summary?.item_count ?? 0}{' '}
                  {manifest.summary?.item_count === 1 ? 'item' : 'items'} withheld
                  under{' '}
                  {(manifest.summary?.grounds ?? [])
                    .map((ground) => `s${ground}`)
                    .join(', ') || 'no recorded ground'}
                </p>
              </div>
              <p className="mt-0.5 text-xs text-gray-400">
                {[
                  manifest.source?.version != null
                    ? `From v${manifest.source.version}`
                    : null,
                  manifest.metadata?.creator,
                  manifest.metadata?.created?.split('T')[0],
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            {downloadHref && (
              <button
                type="button"
                onClick={() => handleDownload(manifest, downloadHref)}
                disabled={downloadingId === manifest.id}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                {downloadingId === manifest.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Download
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
